'use client';

import type { CmsData, CmsDataJsonApi } from '@packages/cms-components/interfaces';
import { LazyRender } from '@packages/modules/src/LazyRender/LazyRender';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { CategoryPageDefaultContentFragmentFragmentDoc } from '@packages/gql/generated/shopping/CategoryPageDefaultContentFragmentFragmentDoc';
import type { ProductListSeoFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductListSeoFragmentFragmentDoc';
import { ContentExternal } from '@packages/cms-components/src/modules/ContentExternal/ContentExternal';
import { LastSeenProductsRecoSlider } from '@packages/recommendations/src/components/ProductsRecoSliders/LastSeenProductsRecoSlider/LastSeenProductsRecoSlider.client';
import type { Device } from '@packages/shared/src/utils/types';

import { ErrorBoundary } from '@packages/shared/src/ui/ErrorBoundary/ErrorBoundary';
import { BypassBlock } from '@packages/shared/src/ui/BypassBlock/BypassBlock';
import { defineMessages, useIntl } from 'react-intl';
import { Skeleton } from '@packages/shared/src/ui/Skeleton/Skeleton';
import { ProductListPageLayout } from '../ProductListPageLayout/ProductListPageLayout.client';
import { ProductListBreadcrumbs } from '../ProductListBreadcrumbs/ProductListBreadcrumbs';
import { ProductListSeo } from '../ProductListSeo/ProductListSeo';
import { ProductCategoryNavigationDesktop } from '../ProductCategoryNavigationDesktop/ProductCategoryNavigationDesktop.client';
import { SeoProductCategoryNavigation } from '../SeoProductCategoryNavigation/SeoProductCategoryNavigation';
import { ProductCategoryNavigationMobile } from '../ProductCategoryNavigationMobile/ProductCategoryNavigationMobile';
import { CmsTopBanner } from '../CmsTopBanner/CmsTopBanner';
import { useDisplayMode } from '../../hooks/useDisplayMode';
import { bypassProductListPageLayoutMessages } from '../../bypassBlocks.messages';
import { DisplayCategoryTracking } from '../DisplayCategoryTracking/DisplayCategoryTracking';
import { SearchKimRatingDynamic } from '../SearchKimRating/SearchKimRating.dynamic';

/* GraphQL */ `
  fragment CategoryPageDefaultContentFragment on CategoryResult {
    ...ProductListPageLayoutFragment
    ...ProductListBreadcrumbsFragment
    ...ProductCategoryNavigationMobileWorkaroundFragment
    ...SearchKimRatingFragment
    ...SearchKimRatingCategoryPageFragment
    ...UseDisplayModeFragment
    ...UseDisplayCategoryTrackingFragment

    seoCategoryNavigation {
      ...SeoProductCategoryNavigationFragment
    }

    CategoryResultCategoryItems: categories {
      ...ProductCategoryNavigationDesktopFragment
      ...ProductCategoryNavigationMobileFragment
    }
    cmsContentPosition
  }
`;
const messages = defineMessages({
  leftNavigationCategoryLabel: {
    id: 'search.sidebar.navigation.categories.label',
    defaultMessage: 'Kategorienavigation',
  },
});

export type CategoryPageDefaultContentProps = {
  maskedData: FragmentType<typeof CategoryPageDefaultContentFragmentFragmentDoc>;
  topLevelCategories?: string[];
  bannerData?: CmsDataJsonApi;
  cmsContent?: CmsData;
  maskedSeoResponse?: FragmentType<typeof ProductListSeoFragmentFragmentDoc>;
  device: Device;
  referrer?: string;
};

/**
 * CategoryPageDefaultContent component description displayed in storybook
 * */
export const CategoryPageDefaultContent = ({
  maskedData,
  topLevelCategories,
  bannerData,
  cmsContent,
  maskedSeoResponse,
  device,
  referrer,
}: CategoryPageDefaultContentProps) => {
  const data = unmask<typeof CategoryPageDefaultContentFragmentFragmentDoc>(maskedData);
  const { formatMessage } = useIntl();

  const { displayMode } = useDisplayMode(data);

  const { cmsContentPosition } = data;

  return (
    <>
      <ErrorBoundary>
        <DisplayCategoryTracking maskedResult={data} />
      </ErrorBoundary>

      <ProductListPageLayout
        leftSidebarAriaLabel={formatMessage(messages.leftNavigationCategoryLabel)}
        maskedData={data}
        topLevelCategories={topLevelCategories}
        device={device}
        referrer={referrer}
        enablePRankTracking
        aboveMainContentComponent={
          <>
            <ProductListBreadcrumbs maskedData={data} />
            {bannerData && cmsContentPosition !== 'TOP' && <CmsTopBanner bannerData={bannerData} />}
            {cmsContent && cmsContentPosition === 'TOP' && (
              <LazyRender fallback={<Skeleton className="h-[200px] w-full" />}>
                <ErrorBoundary>
                  <ContentExternal data={cmsContent} />
                </ErrorBoundary>
              </LazyRender>
            )}
          </>
        }
        belowProductsComponent={
          <>
            {cmsContent && cmsContentPosition === 'BOTTOM' && <ContentExternal data={cmsContent} />}

            <ErrorBoundary>
              <LastSeenProductsRecoSlider fetchOnThreshold title="" />
            </ErrorBoundary>

            <ErrorBoundary>
              <SearchKimRatingDynamic
                maskedData={data}
                maskedCategoryPageData={data}
                searchPageType="MBA"
                displayMode={displayMode}
                templateParams={{ x17: referrer ?? 'x' }}
              />
            </ErrorBoundary>

            {maskedSeoResponse && <ProductListSeo maskedData={maskedSeoResponse} />}
          </>
        }
        leftSidebarDesktopComponent={
          <div className="flex flex-col gap-1">
            <BypassBlock
              buttonText={formatMessage(bypassProductListPageLayoutMessages.categoryNavigation)}
              skipScrollIntoView
            >
              <ProductCategoryNavigationDesktop maskedItems={data.CategoryResultCategoryItems} />
              {data.seoCategoryNavigation && (
                <>
                  <hr className="opacity-15" />
                  <SeoProductCategoryNavigation maskedNavigation={data.seoCategoryNavigation} />
                </>
              )}
            </BypassBlock>
          </div>
        }
        renderCategoryDialogContent={(closeDialog) => (
          <ProductCategoryNavigationMobile
            maskedItems={data.CategoryResultCategoryItems}
            maskedResult={data}
            onNavigate={closeDialog}
          />
        )}
      />
    </>
  );
};
